/* meinbecher 3D customizer
 *
 * Wires together:
 *   - Fabric.js  →  2D layer editor (image upload, text, drag/resize)
 *   - Three.js   →  3D mug preview with the editor canvas as texture
 *   - Shopify Cart  →  AJAX add with line-item properties carrying the print
 *
 * No external assets required — the mug is procedural.
 */

(function () {
  'use strict';

  // 2D editor canvas — matches the full unrolled cylinder so the print
  // wraps all the way around the mug. Aspect = 2πR / visibleH ≈ 2.9 : 1.
  const CANVAS_W = 870;
  const CANVAS_H = 300;

  function init() {
    const root = document.querySelector('[data-customizer]');
    if (!root || typeof THREE === 'undefined' || typeof fabric === 'undefined') {
      return requestAnimationFrame(init);
    }

    const productHandle = root.dataset.productHandle || 'wunschmotiv-tasse-selbst-gestaltet';

    // ----- Fabric: the 2D print area ----------------------------------------
    const editCanvasEl = document.getElementById('mb-canvas');
    editCanvasEl.width = CANVAS_W;
    editCanvasEl.height = CANVAS_H;

    const fab = new fabric.Canvas(editCanvasEl, {
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
    });

    // The dashed guide is a CSS overlay (.mb-customizer__editor-guide) so it
    // doesn't end up baked into the texture that wraps the 3D mug.
    // The handle sits at the texture seam (canvas left/right edge), so the
    // whole editable canvas is genuinely printable — no in-canvas block zone.
    fab.requestRenderAll();

    // CSS handles the visual scaling of Fabric's canvas-container; nothing to wire here.
    // Fabric calculates mouse coordinates from getBoundingClientRect, so as long as the
    // CSS scaling is uniform (aspect-ratio preserved), drag/drop will map correctly.

    // ----- Three.js: 3D mug ------------------------------------------------
    const container = document.getElementById('mb-3d');
    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 100);
    camera.position.set(1.7, 0.5, 7.0);
    camera.lookAt(0, 0, 0);

    // preserveDrawingBuffer keeps the rendered frame readable so we can grab
    // a positioning preview with toDataURL() when the order is submitted.
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    if (THREE.SRGBColorSpace) renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.45;
    container.appendChild(renderer.domElement);

    // Studio-Umgebung (Image-Based Lighting) für realistische Reflexionen
    // auf der glänzenden Keramik – ohne reflektierende Umgebung wirkt der
    // Becher flach. Ein weicher Vertikal-Verlauf + Softbox-Streifen genügt.
    (function setupEnvironment() {
      try {
        if (!THREE.PMREMGenerator) return;
        const pmrem = new THREE.PMREMGenerator(renderer);
        const c = document.createElement('canvas');
        c.width = 128; c.height = 512;
        const g = c.getContext('2d');
        const grad = g.createLinearGradient(0, 0, 0, 512);
        grad.addColorStop(0.00, '#ffffff');
        grad.addColorStop(0.42, '#eef1f6');
        grad.addColorStop(0.55, '#d3d8e1');
        grad.addColorStop(1.00, '#9099a6');
        g.fillStyle = grad; g.fillRect(0, 0, 128, 512);
        // weiche Softbox (heller Lichtstreifen oben)
        g.fillStyle = 'rgba(255,255,255,0.98)';
        g.fillRect(0, 60, 128, 70);
        // zweite, schwächere Lichtquelle seitlich
        g.fillStyle = 'rgba(255,255,255,0.55)';
        g.fillRect(86, 150, 30, 150);
        const tex = new THREE.CanvasTexture(c);
        tex.mapping = THREE.EquirectangularReflectionMapping;
        if (THREE.SRGBColorSpace) tex.colorSpace = THREE.SRGBColorSpace;
        const envRT = pmrem.fromEquirectangular(tex);
        scene.environment = envRT.texture;
        tex.dispose();
        pmrem.dispose();
      } catch (e) { console.warn('Environment-Setup fehlgeschlagen:', e); }
    })();

    function resize3D() {
      const w = container.clientWidth;
      const h = container.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    resize3D();
    new ResizeObserver(resize3D).observe(container);

    // Lights — bright studio setup for shiny ceramic look
    scene.add(new THREE.HemisphereLight(0xffffff, 0xdddddd, 0.55));
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(-3, 3, 3);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.6);
    fill.position.set(3, 1.5, 2);
    scene.add(fill);
    const back = new THREE.DirectionalLight(0xffffff, 0.7);
    back.position.set(0, 2, -3);
    scene.add(back);
    // Specular highlight light (sharp small light for the glossy streak)
    const spec = new THREE.PointLight(0xffffff, 1.2, 8, 1);
    spec.position.set(-2.2, 2.5, 2.8);
    scene.add(spec);

    // ----- Mug texture — full-wrap: dedicated canvas rendered from the
    // Fabric OBJECTS only (not the editor canvas), so selection borders and
    // control handles never leak onto the 3D mug.
    const texCanvas = document.createElement('canvas');
    texCanvas.width = CANVAS_W;
    texCanvas.height = CANVAS_H;
    const texCtx = texCanvas.getContext('2d');
    texCtx.fillStyle = '#ffffff';
    texCtx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    const texture = new THREE.CanvasTexture(texCanvas);
    if (THREE.SRGBColorSpace) texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;

    function updateWrap() {
      texCtx.fillStyle = '#ffffff';
      texCtx.fillRect(0, 0, CANVAS_W, CANVAS_H);
      // object.render() draws only the object itself — no selection UI.
      fab.getObjects().forEach(function (o) {
        o.render(texCtx);
      });
      texture.needsUpdate = true;
    }

    // ----- Mug geometry — proportions match the real 10 oz ceramic mug ------
    const BODY_HEIGHT = 1.95;     // taller — matches the reference closer
    const RADIUS = 0.85;
    const WALL_THICKNESS = 0.04;
    const BEVEL = 0.05;
    const SEG = 128;

    // Glossy ceramic material factory — pure white, high gloss
    const ceramic = (color) => new THREE.MeshPhysicalMaterial({
      color: color,
      roughness: 0.18,
      metalness: 0.0,
      clearcoat: 1.0,
      clearcoatRoughness: 0.09,  // echte Keramik ist nicht spiegelglatt
      reflectivity: 0.55,
      envMapIntensity: 0.9,      // nutzt die Studio-Umgebung für Reflexe
      emissive: 0x070707,        // tiny lift to keep whites looking white
    });

    const mugGroup = new THREE.Group();
    scene.add(mugGroup);

    // Outer wall — shorter cylinder; corners are filled by bevel tori
    const innerH = BODY_HEIGHT - BEVEL * 2;
    const bodyGeom = new THREE.CylinderGeometry(RADIUS, RADIUS, innerH, SEG, 1, true);
    const bodyMat = new THREE.MeshPhysicalMaterial({
      map: texture,
      color: 0xffffff,
      roughness: 0.16,
      metalness: 0.0,
      clearcoat: 1.0,
      clearcoatRoughness: 0.08,
      reflectivity: 0.55,
      envMapIntensity: 0.85,
      emissive: 0x080808,
    });
    const body = new THREE.Mesh(bodyGeom, bodyMat);
    // Three.js cylinder UVs: u=0 → local +Z, u=0.5 → local -Z. Rotating the
    // body by π maps the canvas centre (u=0.5) to world +Z (facing the
    // camera) and the seam (u=0/1) to world -Z (back) — exactly where the
    // handle sits, so the seam is hidden behind the handle.
    body.rotation.y = Math.PI;
    mugGroup.add(body);

    // Top rounded bevel (torus at top edge — visually soft transition)
    const topBevelGeom = new THREE.TorusGeometry(RADIUS - BEVEL, BEVEL, 32, SEG);
    const topBevel = new THREE.Mesh(topBevelGeom, ceramic(0xffffff));
    topBevel.rotation.x = Math.PI / 2;
    topBevel.position.y = innerH / 2;
    mugGroup.add(topBevel);

    // Bottom rounded bevel
    const bottomBevel = new THREE.Mesh(topBevelGeom, ceramic(0xffffff));
    bottomBevel.rotation.x = Math.PI / 2;
    bottomBevel.position.y = -innerH / 2;
    mugGroup.add(bottomBevel);

    // Top rim cap — overlaps the bevel slightly so no seam shows
    const rimShape = new THREE.RingGeometry(RADIUS - WALL_THICKNESS, RADIUS - BEVEL * 0.5, SEG);
    const rim = new THREE.Mesh(rimShape, ceramic(0xffffff));
    rim.rotation.x = -Math.PI / 2;
    rim.position.y = BODY_HEIGHT / 2 - 0.001;
    mugGroup.add(rim);

    // Inside wall
    const innerGeom = new THREE.CylinderGeometry(
      RADIUS - WALL_THICKNESS, RADIUS - WALL_THICKNESS,
      BODY_HEIGHT - 0.08, SEG, 1, true
    );
    const innerMat = ceramic(0xf6f6f6);
    innerMat.side = THREE.BackSide;
    const inner = new THREE.Mesh(innerGeom, innerMat);
    mugGroup.add(inner);

    // Cup floor (inner bottom)
    const innerBottom = new THREE.Mesh(
      new THREE.CircleGeometry(RADIUS - WALL_THICKNESS, SEG),
      ceramic(0xf2f2f2)
    );
    innerBottom.rotation.x = -Math.PI / 2;
    innerBottom.position.y = -BODY_HEIGHT / 2 + 0.04;
    mugGroup.add(innerBottom);

    // Outer bottom seal — flush with bottom bevel
    const outerBottom = new THREE.Mesh(
      new THREE.CircleGeometry(RADIUS - BEVEL + 0.001, SEG),
      ceramic(0xffffff)
    );
    outerBottom.rotation.x = Math.PI / 2;
    outerBottom.position.y = -BODY_HEIGHT / 2;
    mugGroup.add(outerBottom);

    // Handle — smooth oval D-loop. Cubic bezier with horizontal-leaving
    // control handles makes the tangent perpendicular to the body wall at
    // both attachments, giving a clean ceramic-mug join.
    const HANDLE_THICKNESS = 0.052;
    const TOP_ATTACH_Y = BODY_HEIGHT * 0.32;
    const BOT_ATTACH_Y = -BODY_HEIGHT * 0.32;
    const handleCurve = new THREE.CubicBezierCurve3(
      new THREE.Vector3(RADIUS - 0.03, TOP_ATTACH_Y, 0),               // top attach
      new THREE.Vector3(RADIUS + 0.95, TOP_ATTACH_Y, 0),               // ctrl exits perpendicular, far out
      new THREE.Vector3(RADIUS + 0.95, BOT_ATTACH_Y, 0),               // ctrl re-enters perpendicular
      new THREE.Vector3(RADIUS - 0.03, BOT_ATTACH_Y, 0),               // bottom attach
    );
    const handleGeom = new THREE.TubeGeometry(handleCurve, 128, HANDLE_THICKNESS, 24, false);
    const handle = new THREE.Mesh(handleGeom, ceramic(0xffffff));
    // Handle sits at the texture seam — world -Z (back of the mug).
    handle.rotation.y = Math.PI / 2;
    mugGroup.add(handle);

    // Soft drop shadow under the mug — placed well below to avoid clipping
    const shadow = new THREE.Mesh(
      new THREE.CircleGeometry(1.05, 48),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.16 })
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = -BODY_HEIGHT / 2 - 0.04;
    scene.add(shadow);

    // ----- Manual rotation controls (no OrbitControls dep) ------------------
    let isDragging = false;
    let lastX = 0, lastY = 0;
    // Start slightly turned so the handle peeks out on the right while the
    // design stays comfortably readable on the front.
    let rotY = -0.5;
    let rotX = 0;
    function onDown(e) {
      isDragging = true;
      const p = e.touches ? e.touches[0] : e;
      lastX = p.clientX; lastY = p.clientY;
    }
    function onMove(e) {
      if (!isDragging) return;
      const p = e.touches ? e.touches[0] : e;
      const dx = p.clientX - lastX;
      lastX = p.clientX; lastY = p.clientY;
      rotY += dx * 0.01;
      // Vertical drag intentionally ignored — prevents the mug from clipping
      // through the floor/shadow plane. The intended interaction is spin only.
    }
    function onUp() { isDragging = false; }
    container.addEventListener('mousedown', onDown);
    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseup', onUp);
    container.addEventListener('mouseleave', onUp);
    container.addEventListener('touchstart', onDown, { passive: true });
    container.addEventListener('touchmove', onMove, { passive: true });
    container.addEventListener('touchend', onUp);

    function tick() {
      mugGroup.rotation.y = rotY;
      updateWrap();
      renderer.render(scene, camera);
      requestAnimationFrame(tick);
    }
    tick();

    // ----- Selection wiring (for text controls + delete) -------------------
    const deleteBtn = document.getElementById('mb-delete');
    const textControls = document.getElementById('mb-text-controls');
    const fontSel = document.getElementById('mb-font');
    const colorIn = document.getElementById('mb-color');
    const sizeIn = document.getElementById('mb-size');

    function onSelection() {
      const obj = fab.getActiveObject();
      deleteBtn.disabled = !obj;
      const isText = obj && obj.type === 'i-text';
      textControls.hidden = !isText;
      if (isText) {
        fontSel.value = obj.fontFamily || 'Arial';
        colorIn.value = obj.fill || '#000000';
        sizeIn.value = obj.fontSize || 48;
      }
    }
    fab.on('selection:created', onSelection);
    fab.on('selection:updated', onSelection);
    fab.on('selection:cleared', onSelection);

    // ----- Toolbar handlers ------------------------------------------------
    document.getElementById('mb-upload').addEventListener('change', function (e) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = function (ev) {
        fabric.Image.fromURL(ev.target.result, function (img) {
          const maxW = fab.getWidth() * 0.6;
          const maxH = fab.getHeight() * 0.8;
          const s = Math.min(maxW / img.width, maxH / img.height, 1);
          img.scale(s);
          img.set({
            left: fab.getWidth() / 2,
            top: fab.getHeight() * 0.45,
            originX: 'center', originY: 'center',
          });
          fab.add(img);
          fab.setActiveObject(img);
          fab.requestRenderAll();
        }, { crossOrigin: 'anonymous' });
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    });

    document.getElementById('mb-add-text').addEventListener('click', function () {
      const txt = new fabric.IText('Dein Text', {
        left: fab.getWidth() / 2,
        top: fab.getHeight() * 0.45,    // slightly above geometric centre
        originX: 'center', originY: 'center',
        fontFamily: 'Arial',
        fontSize: 48,
        fill: '#000000',
      });
      fab.add(txt);
      fab.setActiveObject(txt);
      fab.requestRenderAll();
    });

    deleteBtn.addEventListener('click', function () {
      const obj = fab.getActiveObject();
      if (obj) {
        fab.remove(obj);
        fab.discardActiveObject();
        fab.requestRenderAll();
      }
    });

    fontSel.addEventListener('change', function () {
      const obj = fab.getActiveObject();
      if (obj && obj.type === 'i-text') {
        obj.set('fontFamily', fontSel.value);
        fab.requestRenderAll();
      }
    });
    colorIn.addEventListener('input', function () {
      const obj = fab.getActiveObject();
      if (obj && obj.type === 'i-text') {
        obj.set('fill', colorIn.value);
        fab.requestRenderAll();
      }
    });
    sizeIn.addEventListener('input', function () {
      const obj = fab.getActiveObject();
      if (obj && obj.type === 'i-text') {
        obj.set('fontSize', parseInt(sizeIn.value, 10));
        fab.requestRenderAll();
      }
    });

    // ----- Add to cart -----------------------------------------------------
    const status = document.getElementById('mb-status');
    function setStatus(msg, kind) {
      status.textContent = msg;
      status.hidden = false;
      status.className = 'mb-customizer__status mb-customizer__status--' + kind;
    }

    async function getVariantId(handle) {
      const r = await fetch('/products/' + handle + '.js');
      if (!r.ok) throw new Error('Produkt "' + handle + '" nicht gefunden');
      const product = await r.json();
      return product.variants[0].id;
    }

    // Render the print-ready design as a high-resolution JPEG data URL.
    function exportPrintDataURL() {
      return fab.toDataURL({
        format: 'jpeg',
        quality: 0.9,
        multiplier: 3,   // 870×300 canvas → 2610×900 print file
      });
    }

    // Capture a front-facing 3D render of the finished mug so the merchant
    // sees exactly where the design sits on the cup (PNG keeps the
    // transparent background — no black box around the mug).
    function exportPreviewDataURL() {
      const savedRot = mugGroup.rotation.y;
      mugGroup.rotation.y = 0;          // design centre turned toward the camera
      updateWrap();
      renderer.render(scene, camera);
      const url = renderer.domElement.toDataURL('image/png');
      mugGroup.rotation.y = savedRot;   // restore the live spin position
      return url;
    }

    document.getElementById('mb-add-to-cart').addEventListener('click', async function () {
      try {
        const objs = fab.getObjects();
        if (objs.length === 0) {
          setStatus('Bitte erst ein Bild oder einen Text hinzufügen.', 'err');
          return;
        }
        setStatus('Druckdatei wird vorbereitet…', 'info');

        // Convert both the print file and the 3D positioning preview to
        // real image files (Blobs).
        const designBlob = await (await fetch(exportPrintDataURL())).blob();
        const previewBlob = await (await fetch(exportPreviewDataURL())).blob();
        const variantId = await getVariantId(productHandle);

        // Multipart upload: Shopify hosts each file and stores the line-item
        // property as a CDN URL — no giant base64 blob, viewable in the
        // admin order and the order confirmation e-mail.
        //   Design   → flat unrolled print file for production
        //   Vorschau → rendered mug showing the exact placement
        const fd = new FormData();
        fd.append('id', String(variantId));
        fd.append('quantity', '1');
        fd.append('properties[Design]', designBlob, 'meinbecher-design.jpg');
        fd.append('properties[Vorschau]', previewBlob, 'meinbecher-vorschau.png');

        const resp = await fetch('/cart/add.js', { method: 'POST', body: fd });
        if (!resp.ok) throw new Error('Fehler beim Hinzufügen (' + resp.status + ')');
        setStatus('✓ In den Warenkorb gelegt!', 'ok');
        setTimeout(() => { window.location.href = '/cart'; }, 800);
      } catch (err) {
        console.error(err);
        setStatus('Fehler: ' + err.message, 'err');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
